import { createTheme } from "@mui/material";

// Setup theme values independent of existing theme variables
const adminThemeBase = createTheme();

const adminDefaultTheme = createTheme(adminThemeBase, {
	// Team note: Do not use typography variants other than defined in this theme — these variants cover all the combinations we ever need in the project.
	typography: {
		fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',

		display1: {
			fontSize: 84,
			fontWeight: 700
		},
		display2: {
			fontSize: 70,
			fontWeight: 700
		},
		display3: {
			fontSize: 56,
			fontWeight: 700
		},
		display4: {
			fontSize: 42,
			fontWeight: 700,

			[adminThemeBase.breakpoints.down("md")]: {
				fontSize: 28
			}
		},
		h1: {
			fontSize: 28,
			fontWeight: 700
		},
		h2: {
			fontSize: 19,
			fontWeight: 700
		},
		h3: {
			fontSize: 16,
			fontWeight: 700
		},
		h4: {
			fontSize: 14,
			fontWeight: 700
		},
		h5: {
			fontSize: 12,
			fontWeight: 700
		},
		h6: {
			fontSize: 12,
			fontWeight: 400
		},
		text11: {
			fontSize: 11,
			fontWeight: 400
		},
		text12: {
			fontSize: 12,
			fontWeight: 400
		},
		text12Bold: {
			fontSize: 12,
			fontWeight: 700
		},
		text12Uppercase: {
			fontSize: 12,
			fontWeight: 700,
			lineHeight: "20px",
			textTransform: "uppercase"
		},
		text12Italic: {
			fontSize: 12,
			fontWeight: 400,
			fontStyle: "italic"
		},
		text14: {
			fontSize: 14,
			fontWeight: 400
		},
		text14Bold: {
			fontSize: 14,
			fontWeight: 700
		},
		text14Uppercase: {
			fontSize: 14,
			fontWeight: 400,
			textTransform: "uppercase"
		},
		text16: {
			fontSize: 16,
			fontWeight: 400,
			lineHeight: 1.5
		},
		text16Bold: {
			fontSize: 16,
			fontWeight: 700
		},
		text16Uppercase: {
			fontSize: 16,
			fontWeight: 400,
			textTransform: "uppercase"
		},
		text16Underline: {
			fontSize: 16,
			fontWeight: 400,
			textDecoration: "underline"
		},
		text17: {
			fontSize: 17,
			fontWeight: 400
		},
		text17Bold: {
			fontSize: 17,
			fontWeight: 700
		},
		text19: {
			fontSize: 19,
			fontWeight: 400
		},
		text20: {
			fontSize: 20,
			fontWeight: 400
		},
		text20Bold: {
			fontSize: 20,
			fontWeight: 700
		}
	},
	palette: {
		primary: {
			main: "#006CA5",
			light8: "#006CA514",
			light12: "#006CA51F",
			light16: "#006CA529",
			light20: "#006CA529",
			light25: "#006CA540",
			light50: "#006CA580",
			contrastText: "#FFFFFF"
		},
		secondary: {
			main: "#1F2933",
			light8: "#1F293314",
			light12: "#1F29331F",
			light16: "#1F293329",
			light20: "#1F293333",
			light25: "#1F29333D",
			light30: "#1F29334D",
			light50: "#1F293380",
			contrastText: "#FFFFFF"
		},
		text: {
			primary: "#1F2933",
			secondary: "#495057",
			disabled: "#CED4DA"
		},
		common: {
			black: "#000000",
			black2: "rgba(0, 0, 0, 0.02)",
			black4: "rgba(0, 0, 0, 0.04)",
			black5: "rgba(0, 0, 0, 0.05)",
			black8: "rgba(0, 0, 0, 0.08)",
			black12: "rgba(0, 0, 0, 0.12)",
			white: "#FFFFFF"
		},
		grey: {
			50: "#F8F9FA",
			100: "#F6F6F6",
			200: "#E9ECEF",
			300: "#E4E7EB",
			400: "#DEE2E6",
			500: "#CED4DA",
			600: "#ADB5BD",
			700: "#6C757D",
			900: "#1F293354"
		},
		background: {
			default: "#CED3DB",
			paper: "#FFFFFF"
		},
		error: {
			main: "#DC3545",
			dark: "#B70000",
			light: "#FACCCC",
			contrastText: "#FFFFFF"
		},
		info: {
			main: "#00A1C5",
			dark: "#00A3C7",
			light: "#E6F6F9",
			contrastText: "#FFFFFF"
		},
		success: {
			main: "#5CA038",
			dark: "#5CA038",
			light: "#EDF6E8",
			contrastText: "#FFFFFF"
		},
		warning: {
			main: "#FFC107",
			dark: "#F37021",
			light: "#FEF1E9",
			lighter: "#FFF3CD"
		},
		orange: {
			main: "#EE6E20",
			contrastText: "#FFFFFF"
		},
		status: {
			standby: "#007BFF",
			disrupted: "#6610F2"
		},
		border: {
			primary: "#DEE2E6",
			secondary: "#E4E7EB",
			hover: "#C7CDD5",
			active: "#BFC6D0"
		},
		action: {
			selected: "#000000",
			hover: "#F4F5F7"
		},
		divider: "#E4E7EB"
	},
	//Customise shadows so that it can be used as elevation prop on MUI components
	shadows: [
		"none",
		"0px 4px 5px rgba(0, 0, 0, 0.04), 0px 2px 4px rgba(0, 0, 0, 0.03)",
		"0px 6px 30px 5px rgba(0, 0, 0, 0.12), 0px 16px 24px 2px rgba(0, 0, 0, 0.14)",
		...adminThemeBase.shadows
	],
	components: {
		MuiTypography: {
			styleOverrides: {
				root: {
					fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
				}
			},
			defaultProps: {
				component: "p",
				variant: "text16",

				variantMapping: {
					display1: "p",
					display2: "p",
					display3: "p",
					display4: "p",
					h1: "p",
					h2: "p",
					h3: "p",
					h4: "p",
					h5: "p",
					h6: "p",
					text11: "p",
					text12: "p",
					text12Bold: "p",
					text12Italic: "p",
					text12Uppercase: "p",
					text14: "p",
					text14Bold: "p",
					text14Uppercase: "p",
					text16: "p",
					text16Bold: "p",
					text16Uppercase: "p",
					text16Underline: "p",
					text17: "p",
					text17Bold: "p",
					text19: "p",
					text20: "p",
					text20Bold: "p"
				}
			}
		},
		MuiCssBaseline: {
			styleOverrides: {
				"*": {
					scrollbarWidth: "thin"
				}
			}
		}
	}
});

export const adminPortalTheme = (() => {
	let combinedTheme = createTheme(adminDefaultTheme);

	// Access existing theme palettes and other values here using combined Theme (e.g. combinedTeheme.palette.text.primary)
	return createTheme(combinedTheme, {
		components: {
			MuiAppBar: {
				styleOverrides: {
					root: {
						backgroundColor: combinedTheme.palette.text.primary,
						color: combinedTheme.palette.background.paper,
						borderBottom: "none"
					}
				}
			},
			MuiInputBase: {
				styleOverrides: {
					root: {
						// borderRadius: "4px !important",
						// To disable arrows on number fields
						"& input[type=number]::-webkit-inner-spin-button": {
							WebkitAppearance: "none",
							margin: 0
						}
					}
				}
			},
			MuiTablePagination: {
				styleOverrides: {
					root: {
						//To hide the scrollbar appears beside pagination
						overflow: "hidden"
					}
				}
			},
			MuiTabScrollButton: {
				styleOverrides: {
					root: {
						//To stop tabs from moving right when tabs become scrollable
						"&.Mui-disabled": {
							display: "none"
						}
					}
				}
			},
			MuiLinearProgress: {
				styleOverrides: {
					bar: {
						backgroundColor: combinedTheme.palette.orange.main
					}
				}
			},
			MuiTab: {
				styleOverrides: {
					root: {
						color: combinedTheme.palette.background.paper,

						"&.Mui-selected": {
							backgroundColor: combinedTheme.palette.action.selected,
							color: combinedTheme.palette.background.paper
						}
					}
				}
			},
			MuiTabs: {
				styleOverrides: {
					indicator: {
						backgroundColor: combinedTheme.palette.orange.main
					}
				}
			},
			MuiChip: {
				styleOverrides: {
					root: {
						fontSize: 14,
						fontWeight: 700,
						borderRadius: 24
					}
				}
			},
			MuiCard: {
				styleOverrides: {
					root: {
						backgroundColor: combinedTheme.palette.background.paper,
						borderRadius: 8
					}
				}
			},
			MuiButton: {
				styleOverrides: {
					root: {
						// CV2 renders every button bold and sentence-case, but does it ad-hoc at each
						// call site — see `pages/booking/BookingsPage.tsx:1680` in
						// KR.Orca.CommercialVehicles2, and the same two lines repeated in
						// `PersonnelFilter.tsx`. We deliberately match CV2's *appearance* through a
						// different *mechanism*: once here, rather than an `sx` per button. Do not
						// "fix" this back to per-site `sx`.
						//
						// This has to live on `MuiButton.styleOverrides.root` and not on
						// `typography.button`, because `typography.button` also feeds `MuiTab` — putting
						// it there would silently restyle the navigation tabs.
						textTransform: "none",
						fontWeight: 700,

						"&.MuiButton-text": {
							borderRadius: 0
						},

						"&.MuiButton-textPrimary": {
							"&:hover": {
								backgroundColor: combinedTheme.palette.action.hover
							}
						}
					}
				}
			},
			MuiAlert: {
				styleOverrides: {
					standardSuccess: {
						backgroundColor: combinedTheme.palette.success.light,
						color: combinedTheme.palette.success.dark,
						border: 1,
						borderColor: combinedTheme.palette.success.main
					},
					standardError: {
						backgroundColor: combinedTheme.palette.error.light,
						color: combinedTheme.palette.error.dark,
						border: 1,
						borderColor: combinedTheme.palette.error.main
					},
					standardWarning: {
						backgroundColor: combinedTheme.palette.warning.light,
						color: combinedTheme.palette.warning.dark,
						border: 1,
						borderColor: combinedTheme.palette.warning.main
					},
					standardInfo: {
						backgroundColor: combinedTheme.palette.info.light,
						color: combinedTheme.palette.info.dark,
						border: 1,
						borderColor: combinedTheme.palette.info.main
					},

					root: {
						"& .MuiAlert-icon": {
							margin: 0
						},

						"& .MuiAlert-action": {
							padding: 0,
							margin: 0
						},

						"& .MuiAlert-message": {
							padding: 10
						},

						"& .MuiIconButton-root": {
							padding: 0
						}
					}
				}
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
							borderColor: combinedTheme.palette.primary.main
						}
					}
				}
			}
		}
	});
})();
